import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { 
  Bus, Users, MapPin, Calendar, DollarSign, Search, 
  CheckCircle, XCircle, AlertCircle, ChevronRight,
  Building2, Star, Clock, Filter, ArrowLeft, Grid3X3,
  Percent, Settings, Save, Loader2, BarChart3, FileText, Edit, Wallet, KeyRound, TrendingUp
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import CommissionSettingsTab from './bus/CommissionSettingsTab';
import CommissionAnalyticsTab from './bus/CommissionAnalyticsTab';
import CommissionLedgerTab from './bus/CommissionLedgerTab';
import DestinationReportsTab from './bus/DestinationReportsTab';
import PlatformRevenueTab from './bus/PlatformRevenueTab';
import RouteStopsManager from './bus/RouteStopsManager';

import ResetPasswordModal from '../../components/admin/ResetPasswordModal';

const AdminBusManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [operators, setOperators] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  
  // Reset password state
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  
  // Commission state
  const [commissionSettings, setCommissionSettings] = useState(null);
  const [commissionStats, setCommissionStats] = useState(null);
  const [savingCommission, setSavingCommission] = useState(false);
  const [editingOperatorCommission, setEditingOperatorCommission] = useState(null);
  const [newCommissionRate, setNewCommissionRate] = useState('');
  
  // Operator details modal state
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [showOperatorModal, setShowOperatorModal] = useState(false);
  const [operatorDetails, setOperatorDetails] = useState(null);
  const [loadingOperatorDetails, setLoadingOperatorDetails] = useState(false);
  
  // Booking settings state
  const [bookingSettings, setBookingSettings] = useState({
    roundTripDiscount: 5,
    maxPassengersPerBooking: 10,
    advanceBookingDays: 90,
    minBookingHoursBeforeDeparture: 2,
    platformFee: { perSeat: 10, enabled: true, refundable: false }
  });
  const [savingBookingSettings, setSavingBookingSettings] = useState(false);
  
  // Fleet search state
  const [fleetSearch, setFleetSearch] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('tekstar_token');
    const user = JSON.parse(localStorage.getItem('tekstar_user') || '{}');
    if (!token || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [dashboardRes, operatorsRes] = await Promise.all([
        api.get('/admin/bus/dashboard'),
        api.get('/admin/bus/operators')
      ]);
      
      setDashboard(dashboardRes.data.dashboard);
      setOperators(operatorsRes.data.operators || []);
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await api.get('/admin/bus/bookings');
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchRoutes = async () => {
    try {
      const response = await api.get('/admin/bus/routes');
      setRoutes(response.data.routes || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchFleet = async () => {
    try {
      const response = await api.get('/admin/bus/fleet');
      setFleet(response.data.buses || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchCommissionSettings = async () => {
    try {
      const response = await api.get('/admin/bus/commission/settings');
      setCommissionSettings(response.data.settings || {});
      setCommissionStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching commission settings:', error);
    }
  };

  const handleUpdateGlobalCommission = async (defaultRate, minRate, maxRate) => {
    setSavingCommission(true);
    try {
      await api.put(`/admin/bus/commission/settings?default_rate=${defaultRate}&min_rate=${minRate}&max_rate=${maxRate}`);
      toast({
        title: 'Success',
        description: `Default commission rate updated to ${defaultRate}%`,
      });
      fetchCommissionSettings();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update commission',
        variant: 'destructive'
      });
    } finally {
      setSavingCommission(false);
    }
  };

  const handleUpdateOperatorCommission = async (operatorId, rate) => {
    try {
      await api.put(`/admin/bus/operators/${operatorId}/commission?commission_rate=${rate}`);
      toast({
        title: 'Success',
        description: 'Operator commission updated',
      });
      setEditingOperatorCommission(null);
      setNewCommissionRate('');
      fetchData();
      fetchCommissionSettings();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update commission',
        variant: 'destructive'
      });
    }
  };

  const handleApplyDefaultToAll = async () => {
    if (!window.confirm('Apply default commission rate to all operators without a custom rate?')) return;
    
    try {
      const response = await api.post('/admin/bus/commission/apply-default');
      toast({
        title: 'Success',
        description: response.data.message,
      });
      fetchData();
      fetchCommissionSettings();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to apply default commission',
        variant: 'destructive'
      });
    }
  };

  // Fetch operator details for modal
  const handleViewOperatorDetails = async (operator) => {
    setSelectedOperator(operator);
    setShowOperatorModal(true);
    setLoadingOperatorDetails(true);
    
    try {
      // Fetch operator's buses, routes, and recent bookings
      const [busesRes, routesRes, bookingsRes] = await Promise.all([
        api.get(`/admin/bus/operators/${operator.id}/fleet`).catch(() => ({ data: { buses: [] } })),
        api.get(`/admin/bus/operators/${operator.id}/routes`).catch(() => ({ data: { routes: [] } })),
        api.get(`/admin/bus/bookings?operatorId=${operator.id}&limit=10`).catch(() => ({ data: { bookings: [] } }))
      ]);
      
      setOperatorDetails({
        buses: busesRes.data.buses || [],
        routes: routesRes.data.routes || [],
        recentBookings: bookingsRes.data.bookings || []
      });
    } catch (error) {
      console.error('Error fetching operator details:', error);
      setOperatorDetails({ buses: [], routes: [], recentBookings: [] });
    } finally {
      setLoadingOperatorDetails(false);
    }
  };

  const closeOperatorModal = () => {
    setShowOperatorModal(false);
    setSelectedOperator(null);
    setOperatorDetails(null);
  };

  const handleApproveOperator = async (operatorId) => {
    try {
      await api.put(`/admin/bus/operators/${operatorId}/approve`);
      toast({
        title: 'Operator Approved',
        description: 'The operator can now add buses and routes',
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to approve operator',
        variant: 'destructive'
      });
    }
  };

  const handleRejectOperator = async (operatorId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      await api.put(`/admin/bus/operators/${operatorId}/reject?reason=${encodeURIComponent(reason)}`);
      toast({
        title: 'Operator Rejected',
        description: 'The application has been rejected',
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to reject operator',
        variant: 'destructive'
      });
    }
  };

  const handleSuspendOperator = async (operatorId) => {
    const reason = prompt('Enter suspension reason:');
    if (!reason) return;

    try {
      await api.put(`/admin/bus/operators/${operatorId}/suspend?reason=${encodeURIComponent(reason)}`);
      toast({
        title: 'Operator Suspended',
        description: 'The operator has been suspended',
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to suspend operator',
        variant: 'destructive'
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'suspended': return 'bg-red-100 text-red-700';
      case 'rejected': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredOperators = operators.filter(op => {
    if (filter !== 'all' && op.status !== filter) return false;
    if (searchQuery && !op.companyName?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Overview', icon: Bus },
    { id: 'operators', label: 'Operators', icon: Building2 },
    { id: 'fleet', label: 'Fleet & Seat Maps', icon: Grid3X3 },
    { id: 'bookings', label: 'Bookings', icon: Calendar },
    { id: 'routes', label: 'Routes', icon: MapPin },
    { id: 'commission', label: 'Commission', icon: Percent },
    { id: 'settlement', label: 'Settlement', icon: Wallet },
    { id: 'revenue', label: 'Revenue', icon: TrendingUp },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'destinations', label: 'Destinations', icon: FileText },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-6xl mx-auto p-4">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard">
              <ArrowLeft className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-xl font-bold">Bus Management</h1>
              <p className="text-indigo-200 text-sm">Manage bus operators and bookings</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-4 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'bookings') fetchBookings();
                  if (tab.id === 'routes') fetchRoutes();
                  if (tab.id === 'fleet') fetchFleet();
                  if (tab.id === 'commission') fetchCommissionSettings();
                }}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Building2 className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Operators</p>
                    <p className="text-2xl font-bold">{dashboard?.operators?.total || 0}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Pending Approval</p>
                    <p className="text-2xl font-bold">{dashboard?.operators?.pending || 0}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Calendar className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Bookings</p>
                    <p className="text-2xl font-bold">{dashboard?.bookings?.total || 0}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Commission Earned</p>
                    <p className="text-2xl font-bold">K{dashboard?.revenue?.commission?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-4">
                <h3 className="font-semibold mb-3">Fleet Overview</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Buses</span>
                    <span className="font-medium">{dashboard?.fleet?.totalBuses || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Active Routes</span>
                    <span className="font-medium">{dashboard?.fleet?.totalRoutes || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Active Trips</span>
                    <span className="font-medium">{dashboard?.trips?.active || 0}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <h3 className="font-semibold mb-3">Bookings Today</h3>
                <div className="text-center py-4">
                  <p className="text-4xl font-bold text-indigo-600">{dashboard?.bookings?.today || 0}</p>
                  <p className="text-gray-500">new bookings</p>
                </div>
              </Card>

              <Card className="p-4">
                <h3 className="font-semibold mb-3">Revenue</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Revenue</span>
                    <span className="font-medium text-green-600">K{dashboard?.revenue?.total?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Platform Commission</span>
                    <span className="font-medium text-indigo-600">K{dashboard?.revenue?.commission?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </Card>
            </div>

            {/* Pending Operators */}
            {dashboard?.operators?.pending > 0 && (
              <Card className="p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-500" />
                  Pending Operator Applications
                </h3>
                <div className="space-y-3">
                  {operators.filter(op => op.status === 'pending').slice(0, 5).map((op) => (
                    <div key={op.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                      <div>
                        <p className="font-medium">{op.companyName}</p>
                        <p className="text-sm text-gray-500">{op.email}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleApproveOperator(op.id)}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200"
                          onClick={() => handleRejectOperator(op.id)}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Operators Tab */}
        {activeTab === 'operators' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search operators..."
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                {['all', 'pending', 'approved', 'suspended'].map((f) => (
                  <Button
                    key={f}
                    variant={filter === f ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilter(f)}
                    className={filter === f ? 'bg-indigo-600' : ''}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Operators List */}
            <div className="space-y-3">
              {filteredOperators.length === 0 ? (
                <Card className="p-8 text-center">
                  <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No operators found</p>
                </Card>
              ) : (
                filteredOperators.map((op) => (
                  <Card key={op.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{op.companyName}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(op.status)}`}>
                            {op.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">{op.email} • {op.phone}</p>
                        <div className="flex gap-4 mt-2 text-sm text-gray-500">
                          <span>{op.busCount || 0} buses</span>
                          <span>{op.routeCount || 0} routes</span>
                          <span>{op.bookingCount || 0} bookings</span>
                          {op.rating > 0 && (
                            <span className="flex items-center gap-1">
                              <Star className="h-4 w-4 text-yellow-500" />
                              {op.rating.toFixed(1)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {op.status === 'pending' && (
                          <>
                            <Button 
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleApproveOperator(op.id)}
                            >
                              Approve
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              className="text-red-600"
                              onClick={() => handleRejectOperator(op.id)}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {op.status === 'approved' && (
                          <Button 
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                            onClick={() => handleSuspendOperator(op.id)}
                          >
                            Suspend
                          </Button>
                        )}
                        <Button 
                          size="sm"
                          variant="outline"
                          className="text-pink-600 border-pink-300 hover:bg-pink-50"
                          onClick={() => { setResetPasswordUser({ _id: op.userId, name: op.companyName, companyName: op.companyName }); setShowResetPassword(true); }}
                          data-testid={`reset-password-operator-${op.id}`}
                        >
                          <KeyRound className="h-4 w-4 mr-1" />
                          Reset Password
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewOperatorDetails(op)}
                        >
                          View Details
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {/* Fleet Tab */}
        {activeTab === 'fleet' && (
          <div className="space-y-4">
            <Card className="p-4 bg-blue-50 border-blue-200">
              <div className="flex items-center gap-2 text-blue-800">
                <Grid3X3 className="h-5 w-5" />
                <p className="font-medium">Fleet & Seat Map Management</p>
              </div>
              <p className="text-sm text-blue-700 mt-1">
                View all buses in the system and edit their seat layouts
              </p>
            </Card>

            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by registration number, make, model, or operator..."
                value={fleetSearch}
                onChange={(e) => setFleetSearch(e.target.value)}
                className="pl-10"
                data-testid="fleet-search-input"
              />
              {fleetSearch && (
                <button
                  onClick={() => setFleetSearch('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Filtered Fleet List */}
            {(() => {
              const filteredFleet = fleet.filter(bus => {
                if (!fleetSearch.trim()) return true;
                const searchLower = fleetSearch.toLowerCase();
                return (
                  (bus.registrationNumber?.toLowerCase() || '').includes(searchLower) ||
                  (bus.make?.toLowerCase() || '').includes(searchLower) ||
                  (bus.model?.toLowerCase() || '').includes(searchLower) ||
                  (bus.operatorName?.toLowerCase() || '').includes(searchLower)
                );
              });

              if (fleet.length === 0) {
                return (
                  <Card className="p-8 text-center">
                    <Bus className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No buses found</p>
                    <p className="text-sm text-gray-400">Buses will appear here once operators add them</p>
                  </Card>
                );
              }

              if (filteredFleet.length === 0) {
                return (
                  <Card className="p-8 text-center">
                    <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No buses match "{fleetSearch}"</p>
                    <p className="text-sm text-gray-400">Try a different search term</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3"
                      onClick={() => setFleetSearch('')}
                    >
                      Clear Search
                    </Button>
                  </Card>
                );
              }

              return (
                <>
                  {fleetSearch && (
                    <p className="text-sm text-gray-500">
                      Showing {filteredFleet.length} of {fleet.length} buses
                    </p>
                  )}
                  <div className="space-y-3">
                    {filteredFleet.map((bus) => (
                      <Card key={bus.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-blue-100 rounded-lg">
                              <Bus className="h-6 w-6 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-semibold">{bus.make} {bus.model}</p>
                              <p className="text-sm text-gray-500 font-mono">{bus.registrationNumber}</p>
                              <p className="text-sm text-gray-500">{bus.operatorName}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm text-gray-500">Capacity</p>
                              <p className="font-semibold">{bus.capacity || bus.seatLayout?.rows * bus.seatLayout?.seatsPerRow || 'N/A'} seats</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-500">Layout</p>
                              <p className="font-semibold">
                                {bus.seatLayout?.rows || 12}R × {bus.seatLayout?.seatsPerRow || 4}S
                              </p>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => navigate(`/admin/bus/seat-map/${bus.id}`)}
                              className="bg-indigo-600 hover:bg-indigo-700"
                              data-testid={`edit-seat-map-${bus.id}`}
                            >
                              <Grid3X3 className="h-4 w-4 mr-2" />
                              Edit Seat Map
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-4">
            {bookings.length === 0 ? (
              <Card className="p-8 text-center">
                <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No bookings found</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {bookings.map((booking) => (
                  <Card key={booking.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono font-semibold text-indigo-600">{booking.ticketCode}</p>
                        <p className="text-sm text-gray-500">
                          {booking.boarding?.city} → {booking.alighting?.city}
                        </p>
                        <p className="text-sm text-gray-500">
                          {booking.operatorName} • {booking.passengers?.length} passenger(s)
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-lg">K{booking.pricing?.total}</p>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Routes Tab */}
        {activeTab === 'routes' && (
          <div className="space-y-4">
            {selectedRouteId ? (
              <div>
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedRouteId(null)}
                  className="mb-4"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back to Routes
                </Button>
                <RouteStopsManager routeId={selectedRouteId} />
              </div>
            ) : routes.length === 0 ? (
              <Card className="p-8 text-center">
                <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No routes found</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {routes.map((route) => (
                  <Card key={route.id} className="p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">
                          {route.origin?.city} → {route.destination?.city}
                        </p>
                        <p className="text-sm text-gray-500">
                          {route.operatorName} • {route.distanceKm} km • {Math.floor(route.estimatedDuration / 60)}h {route.estimatedDuration % 60}m
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            route.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {route.status}
                          </span>
                          <span className="text-xs text-gray-500">
                            {route.stops?.length || 0} stops
                          </span>
                          <span className="text-xs text-gray-500">
                            {route.pricingModel === 'fixed_per_stop' ? '💰 Fixed' : '📏 Distance'} pricing
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-semibold">K{route.basePrice}</p>
                          <p className="text-xs text-gray-500">base fare</p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setSelectedRouteId(route.id)}
                        >
                          <Edit className="h-4 w-4 mr-1" /> Stops
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Commission Settings Tab */}
        {activeTab === 'commission' && (
          <CommissionSettingsTab 
            commissionSettings={commissionSettings}
            commissionStats={commissionStats}
            operators={operators}
            savingCommission={savingCommission}
            editingOperatorCommission={editingOperatorCommission}
            newCommissionRate={newCommissionRate}
            setEditingOperatorCommission={setEditingOperatorCommission}
            setNewCommissionRate={setNewCommissionRate}
            handleUpdateGlobalCommission={handleUpdateGlobalCommission}
            handleUpdateOperatorCommission={handleUpdateOperatorCommission}
            handleApplyDefaultToAll={handleApplyDefaultToAll}
            getStatusColor={getStatusColor}
          />
        )}

        {/* Commission Settlement/Ledger Tab */}
        {activeTab === 'settlement' && (
          <CommissionLedgerTab />
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <CommissionAnalyticsTab />
        )}

        {/* Platform Revenue Tab */}
        {activeTab === 'revenue' && (
          <PlatformRevenueTab />
        )}

        {/* Destinations Tab */}
        {activeTab === 'destinations' && (
          <DestinationReportsTab />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <BookingSettingsTab 
            settings={bookingSettings}
            setSettings={setBookingSettings}
            saving={savingBookingSettings}
            setSaving={setSavingBookingSettings}
          />
        )}
      </div>

      {/* Operator Details Modal */}
      {showOperatorModal && selectedOperator && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{selectedOperator.companyName}</h2>
                <p className="text-sm text-gray-500">Operator Details</p>
              </div>
              <Button variant="ghost" size="sm" onClick={closeOperatorModal}>
                <XCircle className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="p-4 space-y-6">
              {/* Operator Info */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-gray-500">Status</label>
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-sm ${getStatusColor(selectedOperator.status)}`}>
                    {selectedOperator.status === 'approved' && <CheckCircle className="h-3 w-3 mr-1" />}
                    {selectedOperator.status}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Contact Phone</label>
                  <p className="font-medium">{selectedOperator.phone || selectedOperator.contactPhone || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Email</label>
                  <p className="font-medium">{selectedOperator.email || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Commission Rate</label>
                  <p className="font-medium">{selectedOperator.commissionRate || commissionSettings?.defaultRate || 12}%</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Rating</label>
                  <p className="font-medium flex items-center gap-1">
                    <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    {selectedOperator.rating?.toFixed(1) || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Total Bookings</label>
                  <p className="font-medium">{selectedOperator.totalBookings || 0}</p>
                </div>
              </div>

              {/* Loading State */}
              {loadingOperatorDetails && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                </div>
              )}

              {/* Operator Details */}
              {operatorDetails && !loadingOperatorDetails && (
                <>
                  {/* Buses */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Bus className="h-4 w-4 text-indigo-600" />
                      Fleet ({operatorDetails.buses.length} buses)
                    </h3>
                    {operatorDetails.buses.length === 0 ? (
                      <p className="text-gray-500 text-sm">No buses registered</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {operatorDetails.buses.slice(0, 4).map((bus, idx) => (
                          <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                            <p className="font-medium">{bus.registrationNumber || bus.plateNumber}</p>
                            <p className="text-sm text-gray-600">{bus.make} {bus.model} ({bus.year})</p>
                            <p className="text-xs text-gray-500">{bus.seatCount || bus.totalSeats} seats</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Routes */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-indigo-600" />
                      Routes ({operatorDetails.routes.length} routes)
                    </h3>
                    {operatorDetails.routes.length === 0 ? (
                      <p className="text-gray-500 text-sm">No routes configured</p>
                    ) : (
                      <div className="space-y-2">
                        {operatorDetails.routes.slice(0, 4).map((route, idx) => (
                          <div key={idx} className="border rounded-lg p-3 bg-gray-50 flex items-center justify-between">
                            <div>
                              <p className="font-medium">
                                {route.origin?.city || route.origin} → {route.destination?.city || route.destination}
                              </p>
                              <p className="text-sm text-gray-600">
                                {route.distance_km || route.distanceKm}km • ~{route.estimated_duration || route.estimatedDuration}h
                              </p>
                            </div>
                            <p className="text-indigo-600 font-medium">K{route.base_price || route.basePrice}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Recent Bookings */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-indigo-600" />
                      Recent Bookings ({operatorDetails.recentBookings.length})
                    </h3>
                    {operatorDetails.recentBookings.length === 0 ? (
                      <p className="text-gray-500 text-sm">No recent bookings</p>
                    ) : (
                      <div className="space-y-2">
                        {operatorDetails.recentBookings.slice(0, 5).map((booking, idx) => (
                          <div key={idx} className="border rounded-lg p-3 bg-gray-50 flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{booking.passengerName || 'Passenger'}</p>
                              <p className="text-xs text-gray-500">
                                {booking.route?.origin?.city || 'Origin'} → {booking.route?.destination?.city || 'Destination'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-indigo-600">K{booking.totalPrice?.toFixed(2)}</p>
                              <p className={`text-xs px-2 py-0.5 rounded-full ${
                                booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                                booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {booking.status}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-3">
              <Button variant="outline" onClick={closeOperatorModal}>
                Close
              </Button>
              {selectedOperator.status === 'pending' && (
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    handleApproveOperator(selectedOperator.id);
                    closeOperatorModal();
                  }}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve Operator
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <ResetPasswordModal
        isOpen={showResetPassword}
        onClose={() => setShowResetPassword(false)}
        user={resetPasswordUser}
        userType="bus_operator"
      />
    </div>
  );
};

// Booking Settings Tab Component
const BookingSettingsTab = ({ settings, setSettings, saving, setSaving }) => {
  const [localSettings, setLocalSettings] = useState(settings);
  
  useEffect(() => {
    fetchBookingSettings();
  }, []);
  
  const fetchBookingSettings = async () => {
    try {
      const response = await api.get('/admin/bus/booking-settings');
      if (response.data.settings) {
        setLocalSettings(response.data.settings);
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching booking settings:', error);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put('/admin/bus/booking-settings', null, {
        params: {
          round_trip_discount: localSettings.roundTripDiscount,
          max_passengers: localSettings.maxPassengersPerBooking,
          advance_booking_days: localSettings.advanceBookingDays,
          min_booking_hours: localSettings.minBookingHoursBeforeDeparture,
          platform_fee_per_seat: localSettings.platformFee?.perSeat ?? 10,
          platform_fee_enabled: localSettings.platformFee?.enabled ?? true,
          platform_fee_refundable: localSettings.platformFee?.refundable ?? false
        }
      });
      
      if (response.data.success) {
        setSettings(localSettings);
        toast({
          title: 'Settings Saved',
          description: 'Booking settings updated successfully',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save settings',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Settings className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Booking Settings</h2>
            <p className="text-sm text-gray-500">Configure global booking parameters</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Round Trip Discount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Round Trip Discount (%)
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={localSettings.roundTripDiscount}
                onChange={(e) => setLocalSettings({
                  ...localSettings,
                  roundTripDiscount: parseFloat(e.target.value) || 0
                })}
                min={0}
                max={50}
                step={0.5}
                className="w-32"
                data-testid="round-trip-discount-input"
              />
              <span className="text-gray-500">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Discount applied to return journey for round trips
            </p>
          </div>
          
          {/* Platform Processing Fee (per seat) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Processing Fee (K per seat)
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={localSettings.platformFee?.perSeat ?? 10}
                onChange={(e) => setLocalSettings({
                  ...localSettings,
                  platformFee: {
                    ...(localSettings.platformFee || {}),
                    perSeat: parseInt(e.target.value) || 0
                  }
                })}
                min={0}
                max={1000}
                step={1}
                className="w-32"
                disabled={localSettings.platformFee?.enabled === false}
                data-testid="processing-fee-input"
              />
              <span className="text-gray-500">Kwacha / seat</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Tekstar platform fee charged per booked seat
            </p>
            <div className="flex items-center gap-6 mt-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <Switch
                  checked={localSettings.platformFee?.enabled ?? true}
                  onCheckedChange={(v) => setLocalSettings({
                    ...localSettings,
                    platformFee: { ...(localSettings.platformFee || {}), enabled: v }
                  })}
                  data-testid="processing-fee-enabled-switch"
                />
                Enabled
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <Switch
                  checked={localSettings.platformFee?.refundable ?? false}
                  onCheckedChange={(v) => setLocalSettings({
                    ...localSettings,
                    platformFee: { ...(localSettings.platformFee || {}), refundable: v }
                  })}
                  data-testid="processing-fee-refundable-switch"
                />
                Refundable on cancellation
              </label>
            </div>
          </div>
          
          {/* Max Passengers */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Passengers per Booking
            </label>
            <Input
              type="number"
              value={localSettings.maxPassengersPerBooking}
              onChange={(e) => setLocalSettings({
                ...localSettings,
                maxPassengersPerBooking: parseInt(e.target.value) || 1
              })}
              min={1}
              max={50}
              className="w-32"
              data-testid="max-passengers-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum number of passengers in a single booking
            </p>
          </div>
          
          {/* Advance Booking Days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Advance Booking Period (days)
            </label>
            <Input
              type="number"
              value={localSettings.advanceBookingDays}
              onChange={(e) => setLocalSettings({
                ...localSettings,
                advanceBookingDays: parseInt(e.target.value) || 30
              })}
              min={7}
              max={365}
              className="w-32"
              data-testid="advance-booking-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              How many days in advance users can book
            </p>
          </div>
          
          {/* Min Hours Before Departure */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Min Hours Before Departure
            </label>
            <Input
              type="number"
              value={localSettings.minBookingHoursBeforeDeparture}
              onChange={(e) => setLocalSettings({
                ...localSettings,
                minBookingHoursBeforeDeparture: parseInt(e.target.value) || 0
              })}
              min={0}
              max={24}
              className="w-32"
              data-testid="min-hours-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Cutoff time before departure for online booking
            </p>
          </div>
        </div>
        
        <div className="mt-6 pt-6 border-t flex justify-end">
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="save-booking-settings-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </Card>
      
      {/* Current Settings Preview */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <h3 className="font-medium text-blue-800 mb-2">Current Settings Preview</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <div>
            <span className="text-blue-600">Round Trip Discount</span>
            <p className="font-semibold">{localSettings.roundTripDiscount}%</p>
          </div>
          <div>
            <span className="text-blue-600">Processing Fee</span>
            <p className="font-semibold">
              {localSettings.platformFee?.enabled === false
                ? 'Disabled'
                : `K${localSettings.platformFee?.perSeat ?? 10}/seat`}
              {localSettings.platformFee?.refundable ? ' · refundable' : ''}
            </p>
          </div>
          <div>
            <span className="text-blue-600">Max Passengers</span>
            <p className="font-semibold">{localSettings.maxPassengersPerBooking}</p>
          </div>
          <div>
            <span className="text-blue-600">Advance Booking</span>
            <p className="font-semibold">{localSettings.advanceBookingDays} days</p>
          </div>
          <div>
            <span className="text-blue-600">Min Hours Before</span>
            <p className="font-semibold">{localSettings.minBookingHoursBeforeDeparture}h</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminBusManagement;
